import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb, { type BreadcrumbItem } from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const relatedArticles = slug ? getRelatedArticles(slug) : [];

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 韩影精选`;
    } else {
      document.title = '文章未找到 - 韩影精选';
    }
  }, [article]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-error-warning-line text-4xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: t('breadcrumb.home'), href: '/' },
    { label: t('nav.category'), href: `/category/${article.categorySlug}` },
    { label: article.category },
    { label: article.title },
  ];

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.director,
    },
    datePublished: article.publishDate,
    url: `https://korean-film-review.readdy.ai/article/${article.slug}`,
    inLanguage: 'zh-CN',
  };

  const webPageLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: article.title,
    url: `https://korean-film-review.readdy.ai/article/${article.slug}`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.label,
        item: item.href ? `https://korean-film-review.readdy.ai${item.href}` : undefined,
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
      <script type="application/ld+json">{JSON.stringify(webPageLd)}</script>

      <Navbar />

      <main className="pt-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full whitespace-nowrap cursor-pointer hover:bg-primary-200 transition-colors"
              >
                {article.category}
              </Link>
              <span className="text-xs text-foreground-500">{article.publishDate}</span>
            </div>

            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-6 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-600 mb-8">
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-user-line"></i>
                <strong className="text-foreground-900">{t('article.director')}：</strong>{article.director}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-group-line"></i>
                <strong className="text-foreground-900">{t('article.cast')}：</strong>{article.cast.join('、')}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-calendar-line"></i>
                <strong className="text-foreground-900">{t('article.year')}：</strong>{article.year}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-star-fill text-accent-500"></i>
                <strong className="text-foreground-900">{t('article.rating')}：</strong>{article.rating} {t('rating.score')}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-time-line"></i>
                <strong className="text-foreground-900">{t('article.runtime')}：</strong>{article.runtime}
              </span>
            </div>
          </div>

          <div className="rounded-xl overflow-hidden mb-10">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={article.title}
              className="w-full aspect-[16/9] object-cover"
            />
          </div>

          <div
            className="prose prose-lg max-w-none mb-10 article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex flex-wrap gap-2 mb-12 pt-8 border-t border-background-200">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap"
              >
                #{tag}
              </span>
            ))}
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-10 text-center">
                {t('section.related')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} article={rel} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}